import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { ReactNode } from "react";

interface GlossaryTermProps {
  term: string;
  iast?: string;
  definition: string;
  englishNote: string;
  children: ReactNode;
}

/**
 * A dotted-underline term that opens a small popover with a plain-language
 * definition and a one-line "why English fails here" note.
 */
export const GlossaryTerm = ({
  term,
  iast,
  definition,
  englishNote,
  children,
}: GlossaryTermProps) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        className="underline decoration-dotted decoration-accent decoration-1 underline-offset-[5px] transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        aria-label={`Definition of ${term}`}
      >
        {children}
      </button>
    </PopoverTrigger>
    <PopoverContent
      align="center"
      sideOffset={8}
      className="w-72 border-border bg-card p-4 shadow-lg"
    >
      <p className="font-display text-base font-medium text-foreground">
        {term}
        {iast && (
          <span className="ml-2 text-sm font-normal italic text-ink-soft">
            {iast}
          </span>
        )}
      </p>
      <p className="mt-2 text-sm leading-relaxed text-foreground">{definition}</p>
      <p className="mt-3 border-t border-border/60 pt-3 text-xs italic leading-relaxed text-muted-foreground">
        <span className="font-medium not-italic text-ink-soft">
          Why English fails here:
        </span>{" "}
        {englishNote}
      </p>
    </PopoverContent>
  </Popover>
);

export default GlossaryTerm;
